import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Sparkles, Layers } from 'lucide-react';
import ServicePage from '../components/Home/Services';
import GiftingProducts from '../components/Home/GiftingProducts';

import heroGlow from '../assets/services/Glow Sign Board/Glow Sign Board.jpg';
import heroAcp from '../assets/services/ACP  BOARD/ACP  BOARD.jpg';
import heroNeon from '../assets/services/NEON SIGNAGE/NEON SIGNAGE.jpg';

function useHeroReveal() {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const id = requestAnimationFrame(() => setVisible(true));
    return () => cancelAnimationFrame(id);
  }, []);

  return [ref, visible];
}

function ServicesHero() {
  const [heroRef, heroVisible] = useHeroReveal();

  return (
    <section
      ref={heroRef}
      className="relative min-h-[48vh] md:min-h-[52vh] flex items-center overflow-hidden bg-surface"
      aria-labelledby="services-page-title"
    >
      <div className="absolute inset-0 bg-gradient-hero-glow opacity-90" aria-hidden />
      <div
        className="absolute top-1/4 -right-32 w-[min(70vw,420px)] h-[min(70vw,420px)] rounded-full bg-gradient-to-br from-brand-orange/25 via-brand-amber/10 to-transparent blur-3xl animate-glow-orbs"
        aria-hidden
      />
      <div
        className="absolute -bottom-24 -left-24 w-[min(85vw,480px)] h-[min(85vw,480px)] rounded-full bg-gradient-to-tr from-brand-yellow/15 via-transparent to-transparent blur-3xl animate-glow-orbs [animation-delay:1.5s]"
        aria-hidden
      />

      <div className="container relative z-10 mx-auto px-4 py-16 md:py-20">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-14 xl:gap-16 items-center">
          <div
            className={`max-w-3xl lg:max-w-none transition-all duration-700 ${
              heroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            <p className="inline-flex items-center gap-2 text-brand-amber text-xs md:text-sm uppercase tracking-[0.22em] font-semibold mb-5">
              <Sparkles className="w-4 h-4 text-brand-yellow shrink-0" aria-hidden />
              Signature catalogue
            </p>

            <h1
              id="services-page-title"
              className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-white leading-[1.08] mb-5"
            >
              Signage that
              <span className="block text-gradient-brand mt-1 drop-shadow-[0_0_32px_rgba(245,158,11,0.2)]">
                performs in the wild
              </span>
            </h1>

            <div
              className={`h-1 w-24 md:w-32 rounded-full bg-gradient-brand mb-6 origin-left scale-x-0 ${
                heroVisible ? 'animate-hero-line' : ''
              }`}
              aria-hidden
            />

            <p className="text-lg md:text-xl text-gray-400 leading-relaxed mb-8 max-w-2xl">
              Explore our core fabrication categories—dimensional letters, ACP facades, glow, neon,
              and programmable LED. Each category opens as a full-width story; click through for
              variants, then reach out for a tailored quote.
            </p>

            <div className="flex flex-wrap gap-3">
              <Link
                to="/contact"
                className="inline-flex items-center justify-center rounded-full bg-gradient-brand px-7 py-3 text-sm font-bold text-black shadow-brand-glow hover:opacity-95 transition-opacity"
              >
                Request a quote
              </Link>
              <a
                href="https://wa.me/919821466536"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center rounded-full border border-white/15 bg-white/5 px-7 py-3 text-sm font-semibold text-white hover:border-brand-amber/40 hover:bg-brand-orange/10 transition-colors"
              >
                WhatsApp us
              </a>
            </div>
          </div>

          {/* Desktop: animated work sampler — real installs from your catalogue */}
          <div
            className={`relative hidden lg:block min-h-[380px] xl:min-h-[440px] transition-all duration-[900ms] delay-100 ${
              heroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
            }`}
            aria-hidden
          >
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[118%] max-w-none h-[85%] rounded-[2rem] bg-gradient-to-br from-brand-orange/25 via-brand-amber/10 to-transparent blur-3xl opacity-90 animate-glow-orbs" />
            <div className="relative mx-auto max-w-lg xl:max-w-xl">
              <p className="absolute -top-2 left-0 z-20 inline-flex items-center gap-2 rounded-full border border-white/10 bg-black/40 backdrop-blur-md px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.15em] text-brand-yellow">
                <Layers className="w-3.5 h-3.5" />
                Live finishes
              </p>

              <div
                className="relative mt-8 rounded-[1.35rem] overflow-hidden ring-1 ring-white/15 shadow-card-premium-hover animate-float-slow"
                style={{ animationDelay: '0.3s' }}
              >
                <img
                  src={heroGlow}
                  alt=""
                  className="w-full h-[220px] xl:h-[260px] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-transparent to-transparent" />
                <span className="absolute bottom-3 left-3 text-[11px] font-semibold text-white/90 tracking-wide">
                  Glow &amp; facia lighting
                </span>
              </div>

              <div
                className="absolute -right-2 xl:right-0 top-[18%] w-[42%] max-w-[200px] rounded-xl overflow-hidden ring-1 ring-white/20 shadow-xl animate-float-slow"
                style={{ animationDelay: '1.2s' }}
              >
                <img src={heroAcp} alt="" className="w-full h-[120px] xl:h-[140px] object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
                <span className="absolute bottom-2 left-2 right-2 text-[9px] font-bold uppercase tracking-wider text-brand-amber">
                  ACP
                </span>
              </div>

              <div
                className="absolute -left-1 xl:-left-2 bottom-[8%] w-[46%] max-w-[210px] rounded-xl overflow-hidden ring-1 ring-white/20 shadow-xl animate-float-slow"
                style={{ animationDelay: '2.1s' }}
              >
                <img src={heroNeon} alt="" className="w-full h-[115px] xl:h-[130px] object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/45 to-transparent" />
                <span className="absolute bottom-2 left-2 right-2 text-[9px] font-bold uppercase tracking-wider text-brand-yellow">
                  Neon &amp; LED
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div
        className="pointer-events-none absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-brand-amber/40 to-transparent"
        aria-hidden
      />
    </section>
  );
}

function Services() {
  return (
    <div className="bg-surface">
      <ServicesHero />
      <ServicePage variant="page" />
      <GiftingProducts variant="page" />
    </div>
  );
}

export default Services;
